import clsx from 'clsx'
import React, { useCallback, useContext, useRef, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import * as Analytics from 'analytics/analytics'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import BedbankRoomOptionsDesktop from './BedbankRoomOptionsDesktop'
import BedbankSidebarFilters from './BedbankSidebarFilters'
import Pane from 'components/Common/Pane'
import BedbankOfferIntro from './BedbankOfferIntro'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { CALENDAR_GA_EVENT_ACTION, GA_ACTION_TO_SNOWPLOW_MAP } from 'constants/analytics'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { hasSelectedRooms as checkHasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import BedbankOfferHeader from './BedbankOfferHeader'
import Backdrop from 'components/utils/Backdrop'
import OfferPageGallery from '../OfferPageGallery'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import CalendarDatePickerSelect from './BedbankDatePicker/CalendarDatePickerSelect'
import OfferDatePickerSelect from '../Filters/DatePicker/OfferDatePickerSelect'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import config from 'constants/config'
import Heading from 'LuxKit/Typography/Heading'
import type { InViewHookResponse } from 'react-intersection-observer'
import { getCapacitiesForBedbankOffer } from 'lib/offer/offerUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getPlural } from 'lib/string/pluralize'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import BodyText from 'LuxKit/Typography/BodyText'
import BedbankDurationTabs from './BedbankDurationTabs'
import AnalyticsComponentContext, { AnalyticsComponent } from 'contexts/Analytics/analyticsComponentContext'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BedbankOfferPageStickyFooter from '../BedbankOfferPageStickyFooter'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import BedbankOfferFlightDealBanner from './BedbankOfferFlightDealBanner'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isFlashCalendarEnabled, isAnyCalendarExist, getCanUseMultiRoomUI } from 'selectors/offerPage/bedbankOfferPageSelectors'
import useShareModalConfig from 'hooks/useShareModalConfig'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import BedbankRoomOptionsMobile from './MobileBookingFlow/BedbankRoomOptionsMobile'
import BedbankOfferPageStickyFooterMobile from './MobileBookingFlow/BedbankOfferPageStickyFooterMobile'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import BedbankMobileStickyHeaderFilters from './MobileBookingFlow/BedbankMobileStickyHeaderFilters'
import useBedbankIndividualRoomAutoselection from 'hooks/OfferPage/useBedbankIndividualRoomAutoselection'
import MultiRoomModeToggle from '../MultipleRoomsBooking/MultiRoomModeToggle'
import OfferDesktopConnectingRoomsToggle from '../Filters/ConnectingRooms/OfferDesktopConnectingRoomsToggle'
import { mediaQueryDown } from 'components/utils/breakpoint'
import BedbankDesktopFlightToggle from './BedbankDesktopFlightToggle'
import ContentTagBanner from 'components/Content/ContentTagBanner'
import MaldivesDirectBundleBanner from 'components/OfferPage/CharterBundle/MaldivesDirectBundleBanner'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

const RelativeBlock = styled.div`
  position: relative;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }

  &:has(> :nth-child(3)) > * {
    flex: 1;
  }

  ${mediaQueryDown.desktop} {
    &:has(> :nth-child(4)) > * {
      /* To ensure when they wrap we don't get three filters on the first row and then on on the second row */
      min-width: 34%;
    }
  }
`

interface Props {
  offer: App.BedbankOffer
  activeFilter?: OfferPageFilterType
  flightsEnabled: boolean
  isCalendarEnabled: boolean
  isNightBarEnabled: boolean
  canShowYmalFooter: boolean
  ymalStickyFooterHideTriggerRef: InViewHookResponse['ref']
  ymalStickyFooterShowTriggerRef: InViewHookResponse['ref']
  airport?: App.Airport
  canUseMultiRoomUI: boolean
  rooms: Array<OfferPageRoom>
}

function BedbankOfferContent({
  activeFilter,
  offer,
  flightsEnabled,
  isCalendarEnabled,
  isNightBarEnabled,
  canShowYmalFooter,
  ymalStickyFooterHideTriggerRef,
  ymalStickyFooterShowTriggerRef,
  airport,
  canUseMultiRoomUI,
  rooms,
}: Props) {
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const headlessMode = useAppSelector(state => state.system.headlessMode)

  const packageOptionsAnchor = useRef<HTMLDivElement>(null)

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToOptions = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      scrollToPackageOptions()
    }
  }, [isTabletScreenSizeAndUp, scrollToPackageOptions])
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const onViewPackageOptions = useCallback(() => {
    scrollToPackageOptions()
    trackEvent('VIEW_PACKAGE_OPTIONS_PRESSED')
  }, [scrollToPackageOptions, trackEvent])

  useEffect(() => {
    if (activeFilter) {
      autoScrollToOptions()
    }
  }, [activeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const onUserAnalyticsAction = useCallback((action: string) => {
    const snowplowEvent = GA_ACTION_TO_SNOWPLOW_MAP[action]
    if (snowplowEvent) {
      trackEvent(snowplowEvent)
    }
  }, [trackEvent])

  const closeFilters = useCallback(() => {
    if (activeFilter === 'checkin') {
      onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.CloseFilter)
    }
    dispatch({ type: OfferPageActions.closeFilter })
  }, [onUserAnalyticsAction, activeFilter, dispatch])

  const hasRoomsSelected = checkHasSelectedRooms(rooms)

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    dispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [dispatch])

  const handleCheckAvailabilityClick = useCallback(() => {
    const filter = hasRoomsSelected ? 'checkin' : 'capacity'
    toggleFilter(filter)
    Analytics.trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: undefined }))
  }, [offer, hasRoomsSelected, toggleFilter])

  const capacities = getCapacitiesForBedbankOffer(offer)

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  const location = offer.property.locationTitleOverride ?
    offer.property.locationTitleOverride :
      [offer.property.address.city, offer.property.address.stateProvinceName]
        .filter(Boolean)
        .join(', ')
  const shareModalConfig = useShareModalConfig({
    type: 'offer',
    image: offer.images[0],
    offerId: offer.id,
    utmCampaign: 'webshare',
    location,
    offerType: 'bedbank_hotel',
  })

  useBedbankIndividualRoomAutoselection()

  const [packageViews, fetchingRates] = useBedbankPackageViews(offer)
  const showOfferBanner = offer.flights.airportCode && airport?.code && !fetchingRates && config.FLIGHT_ENABLED && !isAgentHubEnabled
  const shouldShowMaldivesDirectBundleBanner = useMemo(() => {
    return offer.property.address.countryCode.toUpperCase() === 'MV'
  }, [offer.property.address.countryCode])

  return (
    <UserAnalyticsActionContext.Provider value={onUserAnalyticsAction}>
      <AccommodationOfferPageStickyHeaderMobile>
        <BedbankMobileStickyHeaderFilters />
      </AccommodationOfferPageStickyHeaderMobile>
      <RelativeBlock>
        <CSSBreakpoint min="tablet">
          <BedbankOfferHeader
            onViewPackageOptions={onViewPackageOptions}
            onCheckAvailabilityClick={handleCheckAvailabilityClick}
            flightsEnabled={flightsEnabled}
          >
            {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
          </BedbankOfferHeader>
        </CSSBreakpoint>
        <span ref={ymalStickyFooterHideTriggerRef} />
        <OfferPageGallery offer={offer} shareModalConfig={shareModalConfig} />
        <Breakpoint max="mobile">
          <BedbankSidebarFilters offer={offer} />
        </Breakpoint>
        <Pane>
          <BedbankOfferIntro
            onViewPackageOptions={onViewPackageOptions}
            onCheckAvailabilityClick={handleCheckAvailabilityClick}
            flightsEnabled={flightsEnabled}
            shareModalConfig={shareModalConfig}
          />
        </Pane>
      </RelativeBlock>
      <span ref={ymalStickyFooterShowTriggerRef} />
      <Pane tabletPadding="20 0" ref={packageOptionsAnchor} id="Package-Options">
        <SoftOfferPageWalledGarden>
          <CSSBreakpoint max="mobile" dynamic>
            <AnalyticsComponentContext.Provider value={AnalyticsComponent.roomOption}>
              <BedbankRoomOptionsMobile
                packageViews={packageViews}
                fetchingRates={fetchingRates}
                offer={offer}
                onViewPackageOptions={onViewPackageOptions}
              />
            </AnalyticsComponentContext.Provider>
            {!canUseMultiRoomUI && <BedbankOfferPageStickyFooterMobile />}
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet" dynamic>
            <VerticalSpacer gap={20}>
              <LayoutContainer>
                <VerticalSpacer gap={32}>
                  <VerticalSpacer gap={16}>
                    {shouldShowMaldivesDirectBundleBanner && <MaldivesDirectBundleBanner />}
                    <div>
                      <Heading variant="heading3" as="h2">
                        {intl.formatMessage(
                          {
                            id: 'XOq4gJkL8Z',
                            defaultMessage: 'Select your {count, plural, one {{saleUnit}} other {# {saleUnitPlural}}}',
                            description: 'Heading for selecting rooms/packages on bedbank offer page',
                          },
                          {
                            count: rooms.length > 1 ? rooms.length : 1,
                            saleUnit: offer.saleUnit,
                            saleUnitPlural: getPlural(offer.saleUnit, rooms.length),
                          },
                        )}
                      </Heading>
                      {rooms.length > 1 && <BodyText variant="medium">
                        {intl.formatMessage({
                          id: 'dAQMTPjfb2',
                          defaultMessage: 'Choose the same package for all rooms, or customise each room individually.',
                          description: 'Description text for multi-room selection on bedbank offer page',
                        })}
                      </BodyText>}
                    </div>
                    {canUseMultiRoomUI && <Group direction="horizontal"><MultiRoomModeToggle inputsName="room-mode-desktop" /></Group>}
                    <DesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" className={clsx({ open: !!activeFilter })} wrap="wrap">
                      <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={capacities} />
                      {isCalendarEnabled ? <CalendarDatePickerSelect boundaryRef={desktopFiltersWrapperRef} /> : <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />}
                      {rooms.length > 1 && <OfferDesktopConnectingRoomsToggle />}
                      {flightsEnabled && <BedbankDesktopFlightToggle />}
                    </DesktopFilterBlock>
                    <Backdrop onClick={closeFilters} visible={!!activeFilter} data-testid="filter-backdrop" />
                  </VerticalSpacer>
                  {isNightBarEnabled && <CSSBreakpoint min="tablet" dynamic>
                    <VerticalSpacer gap={8}>
                      <BodyText variant="large" align="center">
                        {intl.formatMessage({
                          id: '5vn2X2mnHe',
                          defaultMessage: 'Select duration',
                          description: 'Heading for duration selection on bedbank offer page',
                        })}
                      </BodyText>
                      <BedbankDurationTabs />
                    </VerticalSpacer>
                  </CSSBreakpoint>}
                </VerticalSpacer>
              </LayoutContainer>
              <VerticalSpacer gap={16}>
                <AnalyticsComponentContext.Provider value={AnalyticsComponent.roomOption}>
                  <LayoutContainer>
                    <VerticalSpacer gap={16}>
                      <ContentTagBanner offerId={offer.id} page="offer" />
                      {showOfferBanner && <BedbankOfferFlightDealBanner
                        destinationAirportCode={offer.flights.airportCode!}
                        originAirportCode={airport?.code}
                      />}
                      <BedbankRoomOptionsDesktop
                        packageViews={packageViews}
                        fetchingRates={fetchingRates}
                        offer={offer}
                        onViewPackageOptions={onViewPackageOptions}
                      />
                    </VerticalSpacer>
                  </LayoutContainer>
                  {!canShowYmalFooter &&
                    <BedbankOfferPageStickyFooter
                      onScrollTo={onViewPackageOptions}
                      flightsEnabled={flightsEnabled}
                    />}
                </AnalyticsComponentContext.Provider>
              </VerticalSpacer>
            </VerticalSpacer>
          </CSSBreakpoint>
        </SoftOfferPageWalledGarden>
      </Pane>
    </UserAnalyticsActionContext.Provider>
  )
}

type MappedProps = Pick<Props,
  | 'activeFilter'
  | 'airport'
  | 'offer'
  | 'isCalendarEnabled'
  | 'isNightBarEnabled'
  | 'canUseMultiRoomUI'
  | 'rooms'
> & { open: boolean }

export default withOfferPageAndReduxState((offerState, appState): MappedProps => ({
  airport: offerState.airport,
  offer: offerState.bedbankOffer!,
  activeFilter: offerState.activeFilter,
  isCalendarEnabled: isFlashCalendarEnabled(offerState, appState),
  isNightBarEnabled: isAnyCalendarExist(offerState, appState),
  open: !!offerState.activeFilter,
  rooms: offerState.rooms,
  canUseMultiRoomUI: getCanUseMultiRoomUI(offerState, appState),
}), BedbankOfferContent, preventRenderWithOpenFilter)
